import React from 'react';
import { ShieldCheck, Warning, Prohibit } from '@phosphor-icons/react';

const Chip = ({ label, value, tone = 'neutral', testId }) => {
  const cls = tone === 'ok'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
    : tone === 'bad'
      ? 'border-rose-200 bg-rose-50 text-rose-800'
      : tone === 'warn'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-slate-200 bg-slate-50 text-slate-700';
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] ${cls}`} data-testid={testId}>
      <span className="text-slate-500">{label}</span> <strong>{value}</strong>
    </span>
  );
};

const fmtDob = (d) => (/^\d{8}$/.test(d || '') ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : d || '—');

const RESULT_LABELS = {
  READY_FOR_CONFIRMATION: 'READY FOR CONFIRMATION',
  READY_FOR_CONFIRMATION_WITH_EXCLUSIONS: 'READY FOR CONFIRMATION — with exclusions',
  REFUSED_MIN_NOT_MET: 'REFUSED — minimum valid CERT population not met',
  REFUSED_DOCUMENT: 'REFUSED — source document not extractable',
};

const num = (v, fallback = 0) => (typeof v === 'number' ? v : fallback);

/**
 * Mandatory pre-staging review: ONLY the five canonical CERT fields per
 * member, the source reference (PDF page/block or CSV row), the validation
 * result and any ambiguity notes. Invalid / duplicate rows are marked
 * INVALID — NOT ELIGIBLE FOR CERT and are mandatorily excluded (locked, never
 * overridable) — only the SELECTED valid population can be staged. PHI is
 * shown to the reviewing operator only; nothing here is logged or sent anywhere.
 */
export const CertPopulationReview = ({ review, agencyName }) => {
  if (!review) return null;
  const rows = review.rows || [];
  const minReq = review.min_inquiries ?? 50;
  const sourceCount = num(review.source_count, num(review.received));
  const validCount = num(review.valid_count, num(review.valid));
  const excludedCount = num(review.excluded_count, num(review.invalid));
  const invalidCount = num(review.invalid_count, excludedCount);
  const duplicateCount = num(review.duplicate_count, num(review.duplicates));
  const selectedCount = num(review.selected_for_cert_count, validCount);
  const minPass = selectedCount >= minReq;
  const result = review.result || (review.ok ? 'READY_FOR_CONFIRMATION' : 'REFUSED_MIN_NOT_MET');
  const withExclusions = Boolean(review.requires_exclusions) || excludedCount > 0;

  return (
    <div className="mb-3 rounded-md border border-slate-300 bg-white p-3" data-testid="cert-population-review">
      <h3 className="text-xs font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
        <ShieldCheck size={14} className="text-slate-500" /> Pre-staging review — extracted population
        <span className="ml-auto font-mono text-[10px] text-slate-500" data-testid="cert-review-digest">
          selected digest {String(review.population_digest || '').slice(0, 16)}…
        </span>
      </h3>

      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="cert-review-summary">
        <Chip label="Source" value={review.source_format === 'pdf' ? `PDF · ${review.pages} page(s)` : 'CSV'} testId="cert-review-source" />
        <Chip label="Agency" value={agencyName || '—'} />
        <Chip label="Source members" value={sourceCount} testId="cert-review-received" />
        <Chip label="Valid" value={validCount} tone={validCount > 0 ? 'ok' : 'bad'} testId="cert-review-valid" />
        <Chip label="Invalid" value={invalidCount} tone={invalidCount === 0 ? 'ok' : 'warn'} testId="cert-review-invalid" />
        <Chip label="Duplicates" value={duplicateCount} tone={duplicateCount === 0 ? 'ok' : 'warn'} testId="cert-review-duplicates" />
        <Chip label="Excluded" value={excludedCount} tone={excludedCount === 0 ? 'ok' : 'warn'} testId="cert-review-excluded" />
        <Chip label="Selected for CERT" value={selectedCount} tone={minPass ? 'ok' : 'bad'} testId="cert-review-selected" />
        <Chip label={`Minimum ${minReq}`} value={minPass ? 'PASS' : 'FAIL'} tone={minPass ? 'ok' : 'bad'} testId="cert-review-minimum" />
        <Chip label="Result" value={RESULT_LABELS[result] || result} tone={review.ok ? (withExclusions ? 'warn' : 'ok') : 'bad'} testId="cert-review-result" />
      </div>

      {(review.document_errors || []).concat(review.header_errors || []).length > 0 && (
        <ul className="mb-2 space-y-0.5" data-testid="cert-review-document-errors">
          {(review.document_errors || []).concat(review.header_errors || []).map((e, i) => (
            <li key={i} className="text-[11px] text-rose-700 flex gap-1"><Warning size={12} className="mt-0.5 shrink-0" />{e}</li>
          ))}
        </ul>
      )}

      {withExclusions && (
        <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900" data-testid="cert-review-exclusion-notice">
          <p className="font-semibold flex items-center gap-1">
            <Prohibit size={12} /> {excludedCount} source member(s) failed validation and are EXCLUDED from the CERT population
            (mandatory — an invalid row can never be forced into CERT).
          </p>
          <p className="mt-0.5">
            {selectedCount} of {sourceCount} source members will be staged and transmitted; {excludedCount} will NOT be.
            No value is repaired, inferred or padded. If you want an excluded member included, correct the source
            document and extract again — the review and its exclusions are bound to this exact extraction.
          </p>
        </div>
      )}

      <div className="max-h-80 overflow-auto rounded border border-slate-200">
        <table className="w-full text-[11px]" data-testid="cert-review-table">
          <thead className="bg-slate-50 text-slate-600 sticky top-0">
            <tr>
              <th className="text-left px-2 py-1 font-semibold">Source</th>
              <th className="text-left px-2 py-1 font-semibold">medicaid_id</th>
              <th className="text-left px-2 py-1 font-semibold">first_name</th>
              <th className="text-left px-2 py-1 font-semibold">last_name</th>
              <th className="text-left px-2 py-1 font-semibold">dob (CCYYMMDD)</th>
              <th className="text-left px-2 py-1 font-semibold">gender</th>
              <th className="text-left px-2 py-1 font-semibold">Validation</th>
              <th className="text-left px-2 py-1 font-semibold">CERT population</th>
              <th className="text-left px-2 py-1 font-semibold">Ambiguity / notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ref} className={r.valid ? 'odd:bg-white even:bg-slate-50/50' : 'bg-rose-50'} data-testid={`cert-review-row-${r.ref}`}>
                <td className="px-2 py-1 font-mono text-slate-500 whitespace-nowrap">{r.ref}</td>
                <td className="px-2 py-1 font-mono">{r.medicaid_id || <span className="text-rose-600">—</span>}</td>
                <td className="px-2 py-1">{r.first_name || <span className="text-rose-600">—</span>}</td>
                <td className="px-2 py-1">{r.last_name || <span className="text-rose-600">—</span>}</td>
                <td className="px-2 py-1 font-mono" title={fmtDob(r.dob)}>{r.dob || <span className="text-rose-600">—</span>}</td>
                <td className="px-2 py-1 font-mono">{r.gender || <span className="text-rose-600">—</span>}</td>
                <td className="px-2 py-1">
                  {r.valid
                    ? <span className="text-emerald-700 font-semibold">OK</span>
                    : (
                      <span className="text-rose-700" data-testid={`cert-review-row-${r.ref}-errors`}>
                        <span className="font-semibold">INVALID — NOT ELIGIBLE FOR CERT</span>
                        <br />
                        <span className="font-mono">{r.errors.join(', ')}</span>
                      </span>
                    )}
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {r.valid
                    ? <span className="text-emerald-700" data-testid={`cert-review-row-${r.ref}-selection`}>Selected</span>
                    : (
                      <label className="inline-flex items-center gap-1 text-rose-700" data-testid={`cert-review-row-${r.ref}-selection`}>
                        <input type="checkbox" checked disabled readOnly aria-label="Exclude from CERT population (mandatory)"
                               data-testid={`cert-review-row-${r.ref}-exclude`} />
                        <span>Exclude from CERT population</span>
                        <span className="text-[10px] text-rose-600">· Excluded — invalid source data</span>
                      </label>
                    )}
                </td>
                <td className="px-2 py-1 text-slate-600">
                  {[...(r.notes || []), ...((r.payers && r.payers.length) ? [`payers: ${r.payers.join(' | ')}`] : [])].join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-[11px] text-slate-600" data-testid="cert-review-footnote">
        {review.ok
          ? (withExclusions
            ? `${selectedCount} of ${sourceCount} source members passed the canonical five-field contract and are selected for CERT; ${excludedCount} excluded (never staged, never transmitted). Confirm below only after checking the selected rows AND the excluded rows against the source document.`
            : `All ${selectedCount} members passed the canonical five-field contract. Confirm below only after checking the source agency, member count, Medicaid IDs, names, DOBs and sex values against the source document.`)
          : `Only ${selectedCount} valid unique member(s) remain after excluding ${excludedCount} invalid/duplicate row(s) — fewer than the OMES minimum of ${minReq}. Nothing can be staged from this extraction; correct the source document and extract again (no automatic repair, no guessing, no synthetic padding).`}
      </p>
    </div>
  );
};

export default CertPopulationReview;
